import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import $ from "jquery";
import { CheckCircle, Loader2, QrCode } from "lucide-react";
import UpiCashout from "./UpiCashout";
import VimoUPICashout from "./VimoUPICashout";

export default function UpiCashoutGateway() {
  const merchantdetails = useSelector((s) => s.auth?.employeeDetails);
  const username = merchantdetails?.username;

  // "select" | "fingpay" | "vimo"
  const [gateway, setGateway] = useState("select");

  const { captureFingerprint, loading: rdLoading } = useRDService();

  // ── APIs ─────────────────────────────────────────────────────────────────────
  const { callApi: checkEkycApi }    = useApi(`/aeps/fingpay/ekyc-status/${username || ""}`, "GET");
  const { callApi: createMerchantApi } = useApi("/v1/fingpay/merchant/create", "POST");
  const { callApi: sendOtpApi }      = useApi("/v1/fingpay/ekyc/send-otp", "POST");
  const { callApi: validateOtpApi }  = useApi("/v1/fingpay/ekyc/validate-otp", "POST");
  const { callApi: resendOtpApi }    = useApi("/v1/fingpay/ekyc/resend-otp", "POST");
  const { callApi: biometricApi }    = useApi("/v1/fingpay/ekyc/biometric", "POST");

  // ── Phase: "checking" | "ekyc" | "cashout" ──────────────────────────────────
  const [phase, setPhase]           = useState("checking");

  // ── eKYC step: "mobile" | "otp" ─────────────────────────────────────────────
  const [ekycStep, setEkycStep]     = useState("mobile");

  const [loading, setLoading]       = useState(false);
  const [message, setMessage]       = useState("");
  const [isError, setIsError]       = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp]               = useState("");
  const [showOnlyBio, setShowOnlyBio] = useState(false);

  // ── Check eKYC status on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (username) checkStatus();
  }, [username]);

  const checkStatus = async () => {
    try {
      setPhase("checking");
      const res = await checkEkycApi(null);
      setPhase(res?.ekycDone === true ? "cashout" : "ekyc");
    } catch {
      setPhase("ekyc");
    }
  };

  // ── PID XML parser (same as EkycComponent1) ──────────────────────────────────
  const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    const infoValue = (name) =>
      $xml.find(`Info[name='${name}']`).attr("value") ||
      $xml.find(`Param[name='${name}']`).attr("value") ||
      "0";
    return {
      dc: get("DeviceInfo", "dc"),
      ci: get("Skey", "ci"),
      hmac: $xml.find("Hmac").text() || "0",
      dpID: get("DeviceInfo", "dpId"),
      mc: get("DeviceInfo", "mc"),
      PidDatatype: get("Data", "type"),
      sessionKey: $xml.find("Skey").text() || "0",
      mi: get("DeviceInfo", "mi"),
      rdsID: get("DeviceInfo", "rdsId"),
      errCode: get("Resp", "errCode"),
      errInfo: get("Resp", "errInfo"),
      fCount: get("Resp", "fCount"),
      fType: get("Resp", "fType"),
      iCount: get("Resp", "iCount"),
      iType: get("Resp", "iType"),
      pCount: get("Resp", "pCount"),
      pType: "0",
      srno: infoValue("srno"),
      sysid: infoValue("sysid"),
      ts: infoValue("ts"),
      Piddata: $xml.find("Data").text() || "0",
      qScore: get("Resp", "qScore"),
      nmPoints: get("Resp", "nmPoints"),
      rdsVer: get("DeviceInfo", "rdsVer"),
    };
  };

  const setMsg = (text, error = false) => {
    setMessage(text);
    setIsError(error);
  };

  // ── Step 1: Create merchant ──────────────────────────────────────────────────
  const handleCreateMerchant = async () => {
    if (mobileNumber.trim().length < 10) {
      setMsg("Please enter a valid 10-digit mobile number.", true);
      return;
    }
    setMsg("");

    // Re-check status first — merchant may have already completed eKYC
    try {
      setLoading(true);
      const statusRes = await checkEkycApi(null);
      if (statusRes?.ekycDone === true) {
        setPhase("cashout");
        return;
      }
    } catch {
      // proceed
    } finally {
      setLoading(false);
    }

    try {
      setLoading(true);
      const res = await createMerchantApi(
        null,
        `?username=${username}&mobileNumber=${mobileNumber.trim()}`
      );

      if (
        (res?.statusCode === 10000 || res?.statusCode === 200) &&
        res?.data?.merchantStatus === true
      ) {
        setPhase("cashout");
        return;
      }

      setEkycStep("otp");
      setMsg(res?.message || "Merchant created. Complete OTP and biometric to proceed.");
    } catch (e) {
      setMsg(e?.message || "Merchant creation failed.", true);
    } finally {
      setLoading(false);
    }
  };

  // ── Send OTP ─────────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await sendOtpApi(null, `?username=${username}&mobileNumber=${mobileNumber}`);
      if (res?.statusCode === 10000) {
        setMsg("OTP sent successfully.");
      } else {
        setMsg(res?.message || "Failed to send OTP.", true);
      }
    } catch (e) {
      setMsg(e?.message || "Failed to send OTP.", true);
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ───────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await resendOtpApi(null, `?username=${username}&mobileNumber=${mobileNumber}`);
      if (res?.statusCode === 10000) {
        setMsg("OTP resent successfully.");
      } else {
        setMsg(res?.message || "Failed to resend OTP.", true);
      }
    } catch (e) {
      setMsg(e?.message || "Failed to resend OTP.", true);
    } finally {
      setLoading(false);
    }
  };

  // ── Validate OTP ─────────────────────────────────────────────────────────────
  const handleValidateOtp = async () => {
    if (!otp) { setMsg("Please enter OTP.", true); return; }
    try {
      setLoading(true);
      setMsg("");
      const res = await validateOtpApi(null, `?username=${username}&otp=${otp}`);
      if (res?.statusCode === 10000) {
        setMsg("OTP verified. Now complete biometric.");
      } else if (res?.statusCode === 999) {
        setShowOnlyBio(true);
        setMsg("OTP verified. Please complete biometric to finish eKYC.");
      } else {
        setMsg(res?.message || "OTP verification failed.", true);
      }
      setOtp("");
    } catch (e) {
      setMsg(e?.message || "OTP verification failed.", true);
    } finally {
      setLoading(false);
    }
  };

  // ── Biometric ────────────────────────────────────────────────────────────────
  const handleBiometric = async () => {
    try {
      setLoading(true);
      setMsg("Place your finger on the scanner...");

      const pidXml = await captureFingerprint("E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=");

      if (!pidXml) {
        setMsg("Fingerprint capture failed. Please try again.", true);
        return;
      }

      const payload = parsePidXml(pidXml);
      const res = await biometricApi(payload, `?username=${username}`);

      if (
        res?.statusCode === 10000 &&
        res?.message?.includes("Bank eKyc successfully completed")
      ) {
        setMsg("eKYC completed! Proceeding to UPI Cash Withdrawal...");
        setTimeout(() => setPhase("cashout"), 1500);
        return;
      }

      if (
        res?.statusCode === 10000 &&
        (res?.message?.includes("Please complete Bank eKYC") ||
          res?.message?.includes("Please Send & validate OTP before giving BioMetric") ||
          res?.message === "SUCCESS")
      ) {
        setShowOnlyBio(false);
        setMsg("Please verify OTP again before completing biometric.", true);
        return;
      }

      setMsg(res?.message || "Biometric failed. Please try again.", true);
    } catch (e) {
      setMsg(e?.message || "Biometric failed.", true);
    } finally {
      setLoading(false);
    }
  };

  // ── Gateway selection ────────────────────────────────────────────────────────
  if (gateway === "select") {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="w-full">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#12345A] flex items-center justify-center shrink-0">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">UPI Cash Withdrawal</h1>
              <p className="text-sm text-gray-500">Choose your UPI cashout provider</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => setGateway("vimo")} fingpay
              className="bg-white rounded-2xl shadow-sm border-2 border-slate-200 hover:border-[#12345A] p-6 text-left transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4 group-hover:bg-indigo-200 transition-colors">
                <QrCode className="w-6 h-6 text-indigo-600" />
              </div>
              <h2 className="text-base font-bold text-slate-800 mb-1"> UPI Cashout 1 </h2>
              <p className="text-sm text-slate-500">UPI Cash Withdrawal </p>
            </button>

            <button
              onClick={() => setGateway("fingpay")}
              className="bg-white rounded-2xl shadow-sm border-2 border-slate-200 hover:border-[#12345A] p-6 text-left transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                <QrCode className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-base font-bold text-slate-800 mb-1"> UPI Cashout 2</h2>
              <p className="text-sm text-slate-500">UPI Cash Withdrawal </p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Vimo gateway ─────────────────────────────────────────────────────────────
  if (gateway === "vimo") {
    return <VimoUPICashout onBack={() => setGateway("select")} />;
  }

  // ── Checking ─────────────────────────────────────────────────────────────────
  if (phase === "checking") {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#12345A]" />
        <span className="ml-3 text-slate-600 text-base">Checking eKYC status...</span>
      </div>
    );
  }

  // ── UPI Cashout (eKYC done) ───────────────────────────────────────────────────
  if (phase === "cashout") {
    return <UpiCashout />;
  }

  // ── eKYC flow ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="w-full">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#12345A] flex items-center justify-center shrink-0">
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">UPI Cash Withdrawal</h1>
            <p className="text-sm text-red-600 font-bold">Complete eKYC once to activate UPI cashout</p>
          </div>
        </div>

        {/* Progress steps */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
            ekycStep === "mobile"
              ? "bg-[#12345A] text-white"
              : "bg-green-100 text-green-700"
          }`}>
            {ekycStep !== "mobile"
              ? <CheckCircle className="w-3.5 h-3.5" />
              : <span className="w-4 text-center">1</span>
            }
            Register
          </div>
          <div className="flex-1 h-px bg-slate-200" />
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
            ekycStep === "otp"
              ? "bg-[#12345A] text-white"
              : "bg-slate-100 text-slate-400"
          }`}>
            <span className="w-4 text-center">2</span>
            OTP &amp; Biometric
          </div>
          <div className="flex-1 h-px bg-slate-200" />
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-400">
            <span className="w-4 text-center">3</span>
            UPI Cashout
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">

          {/* ── Step 1: Mobile input ── */}
          {ekycStep === "mobile" && (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Register Merchant</h2>
              <p className="text-sm text-red-600 font-bold mb-5">
                Enter your mobile number linked to your Aadhaar to create your merchant account for UPI Cashout.
              </p>

              <label className="text-sm font-medium text-slate-700 block mb-1.5">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                maxLength={10}
                placeholder="Enter 10-digit mobile number"
                value={mobileNumber}
                onChange={(e) =>
                  setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                onKeyDown={(e) => e.key === "Enter" && handleCreateMerchant()}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base font-semibold tracking-wider focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition mb-4"
              />

              <button
                onClick={handleCreateMerchant}
                disabled={loading || mobileNumber.length !== 10}
                className="w-full bg-[#12345A] hover:bg-[#0f2d50] text-white py-3.5 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Creating..." : "Create Merchant & Continue"}
              </button>
            </>
          )}

          {/* ── Step 2: OTP + Biometric ── */}
          {ekycStep === "otp" && (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">
                {showOnlyBio ? "Biometric eKYC" : "Verify OTP & Biometric"}
              </h2>
              <p className="text-sm text-red-600 font-bold mb-5">
                Mobile: <strong className="text-red-700">{mobileNumber}</strong>
              </p>

              {!showOnlyBio && (
                <>
                  {/* OTP input + verify */}
                  <div className="flex gap-3 mb-4">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-3 text-xl tracking-[0.4em] text-center font-mono focus:outline-none focus:border-[#12345A] transition"
                    />
                    <button
                      onClick={handleValidateOtp}
                      disabled={loading || !otp}
                      className="px-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold disabled:opacity-50 transition"
                    >
                      Verify
                    </button>
                  </div>

                  {/* Send / Resend */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={handleSendOtp}
                      disabled={loading}
                      className="py-3 bg-[#12345A] hover:bg-[#0f2d50] text-white rounded-xl font-medium disabled:opacity-50 transition"
                    >
                      Send OTP
                    </button>
                    <button
                      onClick={handleResendOtp}
                      disabled={loading}
                      className="py-3 border-2 border-[#12345A] text-[#12345A] hover:bg-slate-50 rounded-xl font-medium disabled:opacity-50 transition"
                    >
                      Resend OTP
                    </button>
                  </div>
                </>
              )}

              {/* Biometric */}
              <button
                onClick={handleBiometric}
                disabled={loading || rdLoading}
                className="w-full py-4 bg-[#12345A] hover:bg-[#0f2d50] text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {(loading || rdLoading) && <Loader2 className="w-4 h-4 animate-spin" />}
                {rdLoading ? "Capturing Fingerprint..." : "Complete Biometric eKYC"}
              </button>
            </>
          )}

          {/* Message */}
          {message && (
            <div className={`mt-5 p-4 rounded-xl text-sm text-center font-medium ${
              isError
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-green-50 text-green-700 border border-green-200"
            }`}>
              {message}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
